import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ALERT_KIND_LABELS, type AlertFilter, type AlertPageDTO, type PlatformAlertDTO } from '@bifeai/shared';
import { Badge, Button, Card, CardBody, CardHeader, EmptyState, PageHeader, Skeleton, useToast } from '@bifeai/ui';
import { Bell, ChevronLeft, ChevronRight, Trash2, TriangleAlert } from 'lucide-react';
import { useEffect, useState } from 'react';
import { api } from '../lib/api';
import { useCan } from '../lib/session';
import { formatDateTime, timeAgo } from '../lib/format';

const PAGE = 25;
const FILTERS: [AlertFilter, string][] = [
  ['all', 'Todos'],
  ['unread', 'Sin leer'],
  ['errors', 'Errores'],
];

/**
 * Avisos del panel: ventas, sucursales y franquicias nuevas, renovaciones, pagos rechazados, atrasos.
 * Al entrar, los comunes se dan por vistos. Los de error quedan marcados hasta que se abren.
 */
export function AlertsPage() {
  const qc = useQueryClient();
  const toast = useToast();
  const [filter, setFilter] = useState<AlertFilter>('all');
  const [page, setPage] = useState(1);
  const [open, setOpen] = useState<string | null>(null);
  const can = useCan();
  const manage = can('alerts.manage');
  const canDelete = can('alerts.delete');

  const list = useQuery({
    queryKey: ['billing', 'alerts', filter, page],
    queryFn: () => api.get<AlertPageDTO>(`/platform/billing/alerts?filter=${filter}&page=${page}&pageSize=${PAGE}`),
  });
  const refresh = () => qc.invalidateQueries({ queryKey: ['billing', 'alerts'] });

  // Entrar a Avisos = verlos (salvo los de error).
  useEffect(() => {
    void api
      .post('/platform/billing/alerts/seen')
      .then(refresh)
      .catch(() => undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const del = useMutation({
    mutationFn: (id: string) => api.del(`/platform/billing/alerts/${id}`),
    onSuccess: refresh,
    onError: () => toast.error('No se pudo borrar'),
  });
  const delRead = useMutation({
    mutationFn: () => api.del<{ deleted: number }>('/platform/billing/alerts'),
    onSuccess: async (r) => {
      toast.success(r.deleted === 1 ? '1 aviso borrado' : `${r.deleted} avisos borrados`);
      setPage(1);
      await refresh();
    },
  });

  const openAlert = async (a: PlatformAlertDTO) => {
    setOpen(open === a.id ? null : a.id);
    if (!a.read && manage) {
      await api.post(`/platform/billing/alerts/${a.id}/read`).catch(() => undefined);
      await refresh();
    }
  };

  const d = list.data;
  const pages = d ? Math.max(1, Math.ceil(d.total / d.pageSize)) : 1;

  return (
    <>
      <PageHeader title="Avisos" subtitle="Ventas, sucursales y franquicias nuevas, renovaciones, pagos rechazados y atrasos." />
      <Card>
        <CardHeader
          title={
            <div className="bf-view-switch" role="tablist" aria-label="Filtro">
              {FILTERS.map(([k, l]) => (
                <button
                  key={k}
                  type="button"
                  role="tab"
                  aria-selected={filter === k}
                  onClick={() => {
                    setFilter(k);
                    setPage(1);
                  }}
                >
                  {l}
                  {k === 'errors' && d?.unreadErrors ? ` (${d.unreadErrors})` : ''}
                </button>
              ))}
            </div>
          }
          actions={
            <Button size="sm" icon={<Trash2 />} loading={delRead.isPending} disabled={!canDelete} onClick={() => delRead.mutate()}>
              Borrar los leídos
            </Button>
          }
        />
        <CardBody flush>
          {list.isPending ? (
            <Skeleton style={{ height: 200 }} />
          ) : !d?.items.length ? (
            <EmptyState icon={<Bell />} title={filter === 'errors' ? 'Sin errores' : 'Sin avisos'} />
          ) : (
            <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
              {d.items.map((a) => (
                <li
                  key={a.id}
                  style={{
                    display: 'flex',
                    gap: 12,
                    alignItems: 'flex-start',
                    padding: '12px 18px',
                    borderTop: '1px solid var(--bf-line)',
                    background: a.read ? undefined : a.error ? 'var(--bf-danger-soft)' : 'var(--bf-celeste-soft)',
                  }}
                >
                  <button
                    type="button"
                    onClick={() => void openAlert(a)}
                    style={{ all: 'unset', cursor: 'pointer', flex: 1, minWidth: 0 }}
                    aria-expanded={open === a.id}
                  >
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                      {a.error && <TriangleAlert size={15} color="var(--bf-danger)" aria-hidden />}
                      <span className="bf-cell-main">{a.title}</span>
                      <Badge tone={a.error ? 'danger' : 'neutral'}>{ALERT_KIND_LABELS[a.kind] ?? a.kind}</Badge>
                      {!a.read && <Badge tone={a.error ? 'danger' : 'info'}>Nuevo</Badge>}
                    </div>
                    <div className="bf-cell-sub">
                      {a.body ? `${a.body} · ` : ''}
                      {timeAgo(a.createdAt)}
                    </div>
                    {open === a.id && (
                      <div className="bf-cell-sub" style={{ marginTop: 6 }}>
                        {formatDateTime(a.createdAt)}
                        {a.tenantName ? ` · Cliente: ${a.tenantName}` : ''}
                      </div>
                    )}
                  </button>
                  {canDelete && <Button size="sm" variant="ghost" aria-label="Borrar aviso" icon={<Trash2 />} onClick={() => del.mutate(a.id)} />}
                </li>
              ))}
            </ul>
          )}
        </CardBody>
      </Card>
      {pages > 1 && (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 12, marginTop: 16 }}>
          <Button size="sm" icon={<ChevronLeft />} disabled={page <= 1} onClick={() => setPage(page - 1)}>
            Anterior
          </Button>
          <span className="bf-cell-sub">
            Página {page} de {pages}
          </span>
          <Button size="sm" disabled={page >= pages} onClick={() => setPage(page + 1)}>
            Siguiente <ChevronRight size={16} aria-hidden />
          </Button>
        </div>
      )}
    </>
  );
}
